import { FC } from 'react';
import { Link } from 'react-router-dom-v5-compat';
import { SPACINGS } from 'shared/styles/variables';
import styled from 'styled-components';

import InquiryStatusSticker from 'dashboard/components/InquiryStatusSticker';
import { userDisplayName } from 'dashboard/lib/valueDisplayers/userDisplayName';
import { Inquiry, InquiryStatus } from 'dashboard/models/inquiry';
import { User } from 'dashboard/models/user';
import { Workflow } from 'dashboard/models/workflow';
import { WorkflowRun, WorkflowRunType } from 'dashboard/models/workflow-run';
import { WorkflowVersion } from 'dashboard/models/workflow-version';
import { InquiryState } from 'dashboard/redux/reducers/inquiry';
import { ApiModel } from 'lib/interfaces/api';

const InquiryStatusContainer = styled('div')`
  color: ${({ theme }) => theme.color.text.primary};
`;

export const ManualDecisionComment = styled('p')`
  color: ${({ theme }) => theme.color.text.primary};
  padding-left: ${SPACINGS.base * 2}px;
`;

interface InquiryStatusInfoProps {
  inquiry: Inquiry;
  reviewer: InquiryState['reviewer'];
  reviewerWorkflow?: InquiryState['reviewerWorkflow'];
  reviewerWorkflowRun?: InquiryState['reviewerWorkflowRun'];
  reviewerWorkflowVersion?: InquiryState['reviewerWorkflowVersion'];
  className?: string;
}

const InquiryStatusInfo: FC<InquiryStatusInfoProps> = ({
  inquiry,
  reviewer,
  reviewerWorkflow,
  reviewerWorkflowRun,
  reviewerWorkflowVersion,
  className,
}) => {
  const { 'reviewer-comment': reviewerComment, status } = inquiry.attributes;

  const hasPostCompletionStatus =
    status === InquiryStatus.NeedsReview ||
    status === InquiryStatus.Approved ||
    status === InquiryStatus.Declined;

  return (
    <InquiryStatusContainer className={className}>
      <InquiryStatusSticker status={status} />{' '}
      {hasPostCompletionStatus && (
        <ReviewerDescription
          reviewer={reviewer}
          reviewerComment={reviewerComment}
          reviewerWorkflow={reviewerWorkflow}
          reviewerWorkflowRun={reviewerWorkflowRun}
          reviewerWorkflowVersion={reviewerWorkflowVersion}
        />
      )}
    </InquiryStatusContainer>
  );
};

export default InquiryStatusInfo;

const ReviewerDescription: FC<{
  reviewer: InquiryState['reviewer'];
  reviewerComment?: string;
  reviewerWorkflow?: Workflow;
  reviewerWorkflowRun?: WorkflowRun;
  reviewerWorkflowVersion?: WorkflowVersion;
}> = (props) => {
  const {
    reviewer,
    reviewerComment,
    reviewerWorkflow,
    reviewerWorkflowVersion,
    reviewerWorkflowRun,
  } = props;

  if (reviewer == null) {
    return (
      <>{reviewerComment && <ManualDecisionComment>{reviewerComment}</ManualDecisionComment>}</>
    );
  }

  if (reviewer.type === ApiModel.User) {
    const user = reviewer as User;
    const reviewerName = userDisplayName(user);

    return (
      <>
        by {reviewerName}
        {reviewerComment && <ManualDecisionComment>{reviewerComment}</ManualDecisionComment>}
      </>
    );
  } else if (
    reviewerWorkflow &&
    reviewerWorkflowVersion &&
    reviewer.type === ApiModel.WorkflowRun
  ) {
    const workflowRun = reviewer as WorkflowRun;
    const { name: workflowName } = reviewerWorkflow.attributes;

    if (workflowRun.attributes['run-type'] === WorkflowRunType.Case) {
      const caseActionLink = `/dashboard/case-actions/${reviewerWorkflow.id}/${reviewerWorkflow.relationships['latest-published-version'].data.id}`;
      return (
        <>
          by <Link to={caseActionLink}>Case Action: {workflowName}</Link>
        </>
      );
    } else {
      const workflowRunLink = `/dashboard/workflows/${reviewerWorkflow.id}/runs/${workflowRun.id}`;
      return (
        <>
          by <Link to={workflowRunLink}>Workflow: {workflowName}</Link>
        </>
      );
    }
  } else if (
    reviewerWorkflow &&
    reviewerWorkflowRun &&
    reviewer.type === ApiModel.WorkflowRunStepAction
  ) {
    const { name: workflowName } = reviewerWorkflow.attributes;

    if (reviewerWorkflowRun.attributes['run-type'] === WorkflowRunType.Case) {
      const caseActionLink = `/dashboard/case-actions/${reviewerWorkflow.id}/${reviewerWorkflow.relationships['latest-published-version'].data.id}`;
      return (
        <>
          by <Link to={caseActionLink}>Case Action: {workflowName}</Link>
        </>
      );
    } else {
      const workflowRunLink = `/dashboard/workflows/${reviewerWorkflow.id}/runs/${reviewer.relationships['workflow-run'].data.id}`;
      return (
        <>
          by <Link to={workflowRunLink}>Workflow: {workflowName}</Link>
        </>
      );
    }
  }

  return <></>;
};
